import { Link } from "react-router-dom";
import {
  ArrowRight,
  Mic2,
  MessageCircle,
  Code2,
  PartyPopper,
  ChefHat,
  Sparkles,
  BookOpen,
  Building2,
  Quote,
} from "lucide-react";
import PageHero from "@/components/site/PageHero";
import SectionLabel from "@/components/site/SectionLabel";
import CardMark from "@/components/site/CardMark";
import Seo from "@/components/site/Seo";
import { ASSETS, BRAND, PROMISES } from "@/lib/constants";

const PRINCIPLES = [
  { h: "Message First", p: "Substance over polish. Every word earns its place." },
  { h: "Respect The Stage", p: "We prepare like the room matters — because it does." },
  { h: "Real Judging", p: "Structured rubric. Honest critique. No vanity scoring." },
  { h: "Production Value", p: "Lights, sound, and visuals worthy of the speakers in the room." },
  { h: "Pipeline, Not Confetti", p: "We connect speakers to opportunity — not applause alone." },
  { h: "Open Door, High Bar", p: "Diverse backgrounds welcomed. The standard does not move." },
  { h: "Community Anchored", p: "Built with Colorado Springs leaders, partners, and audiences." },
  { h: "Long Game", p: "A platform — not a one-night event. The work continues after the lights." },
];

const IS_IS_NOT = [
  { is: "A live, judged showcase", isNot: "An open mic night" },
  { is: "A pipeline to opportunity", isNot: "A vanity stage" },
  { is: "Coaching and craft", isNot: "A shortcut to expert status" },
  { is: "A serious standard", isNot: "A pay-to-play event" },
];

// R. Dale Brunet — biographical chapters. Source: founder-supplied bio.
// Stored as data so the order/labels stay easy to update.
const FOUNDER_CHAPTERS = [
  {
    icon: Code2,
    kicker: "Four Decades In Technology",
    body: "Brunet's technology career began long before artificial intelligence became a mainstream conversation. Over more than four decades, he has worked with business systems, software development, database design, operational workflows, networks, automation, and applied technology for organizations ranging from small businesses to larger institutional environments. His experience includes early work connected to the 1984 Louisiana World Exposition, corporate and nonprofit technology roles in New Orleans, and later software engineering work in Colorado Springs with defense-contractor environments supporting complex systems.",
  },
  {
    icon: PartyPopper,
    kicker: "Mardi Gras Productions & The New Orleans Lens",
    body: "In the late 1990s, Brunet worked with Mardi Gras Productions in New Orleans — a professional special-event production company connected to the city's world-famous celebration culture. He developed an in-house event management system, giving him direct experience with live event production, scheduling, coordination, and infrastructure. That matters here: The Next Stage is not a loose open mic. It is being developed as a professionally managed showcase with applications, sponsor relationships, production needs, judging integrity, audience experience, and post-event media value. His New Orleans background — including his work with Mardi Gras Productions and Virtual New Orleans — also shaped a lifelong instinct for combining technology, place, culture, media, and public experience. Years before social media became ordinary, he was already thinking about how real-world culture could be represented, organized, and experienced through digital platforms.",
  },
  {
    icon: ChefHat,
    kicker: "Momma Pearl's Cajun Kitchen",
    body: "After Hurricane Katrina, Brunet and his family relocated to Colorado Springs, where he rebuilt his life and career. He later moved from technology into food entrepreneurship — first through The House Chef of Colorado Springs and then through Momma Pearl's Cajun Kitchen, the Louisiana-inspired restaurant he founded in 2013 and named in honor of his mother, Marie Pearl \u201CMomma Pearl\u201D Theriot. As Chef Robert \u201CBB\u201D Brunet, he became known locally for bringing Southern Louisiana Cajun and Creole food culture to Colorado Springs. Momma Pearl's grew from a small independent restaurant into a larger location near Polaris Pointe, served the local dining community for more than a decade, catered events, built a loyal customer base, received local recognition, and was featured nationally on Food Network's Restaurant: Impossible. Running an independent restaurant for over ten years delivered hard-earned experience in leadership, staffing, customer service, marketing, vendor relationships, cash flow, public reputation, hospitality, logistics, crisis management, and community engagement — every one of which is directly relevant to producing a live, audience-facing show.",
  },
  {
    icon: BookOpen,
    kicker: "Writing, Publishing, AI",
    body: "Following the closure of Momma Pearl's in 2024, Brunet returned full-force to writing, publishing, artificial intelligence, and platform development. Through Use What You Got Publishing, he has authored, developed, and published books across business, AI, entrepreneurship, fiction, social commentary, and personal growth — work that explores how technology, creativity, and human resilience intersect in a rapidly changing world.",
  },
  {
    icon: Sparkles,
    kicker: "AI & Media Platforms",
    body: "Brunet is also the creator and developer behind several AI-centered and media-centered concepts, including Life Capsule AI, NewAudia, Digital Ink AI, and AI-assisted publishing systems. These projects reflect his larger mission: helping people preserve stories, communicate ideas, publish knowledge, and use modern tools to create opportunity.",
  },
];

const FOUNDER_ROLES = [
  "Author",
  "Publisher",
  "AI Technologist",
  "Software Developer",
  "Entrepreneur",
  "Former Restaurateur",
  "Founder / Producer, The Next Stage",
];

export default function About() {
  return (
    <div>
      <Seo />
      <PageHero
        pageTitle="About"
        eyebrowNumber="01"
        eyebrow="About The Next Stage"
        title="Public Speaking Is Not Performance."
        highlight="It Is Leadership In Motion."
        tagline="A platform built for speakers who treat communication as a craft."
        description="We exist to give emerging voices the stage, the standard, and the audience their message deserves."
      />

      {/* FOUNDATIONAL BELIEF */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14 lg:py-20 text-center">
          <SectionLabel number="02" align="center">Foundational Belief</SectionLabel>
          <blockquote className="font-cormorant text-3xl sm:text-4xl lg:text-5xl text-white leading-tight">
            “A powerful message deserves more than a private dream. It deserves
            a room. It deserves an audience. It deserves{" "}
            <span className="gold-text">a stage.</span>”
          </blockquote>
          <p className="font-bebas text-gold tracking-[0.32em] mt-8 text-sm uppercase">
            — {BRAND.founder}, {BRAND.founderTitle}
          </p>
        </div>
      </section>

      {/* VISION + MISSION */}
      <section className="bg-navy border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid lg:grid-cols-2 gap-10">
          <div className="card-dark p-10 corner-bracket">
            <div className="label-bebas text-gold">Our Vision</div>
            <p className="font-cormorant text-2xl sm:text-3xl text-white leading-snug mt-4">
              A region — and a generation — of communicators who treat the
              spoken word as leadership, service, and craft.
            </p>
          </div>
          <div className="card-dark p-10 corner-bracket">
            <div className="label-bebas text-gold">Our Mission</div>
            <p className="font-manrope text-soft-gray leading-relaxed mt-4 text-lg">
              To provide a professionally produced, supportive, and competitive
              platform where emerging speakers sharpen their message,
              strengthen their stage presence, receive meaningful feedback, and
              gain exposure to audiences, sponsors, coaches, publishers, and
              opportunity partners.
            </p>
          </div>
        </div>
      </section>

      {/* IS / IS NOT */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <SectionLabel number="03">What It Is — And Is Not</SectionLabel>
          <h2 className="font-bebas text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-3xl">
            Clarity Beats <span className="gold-text">Hype.</span>
          </h2>
          <div className="mt-8 hairline">
            <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-soft">
              <div className="p-6">
                <div className="label-bebas text-gold">It Is</div>
              </div>
              <div className="p-6">
                <div className="label-bebas text-muted-ink">It Is Not</div>
              </div>
            </div>
            {IS_IS_NOT.map((row, i) => (
              <div
                key={i}
                className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-soft border-t border-slate-soft"
              >
                <div className="p-6 font-shoulders font-bold text-2xl text-white">
                  {row.is}
                </div>
                <div className="p-6 font-manrope text-muted-ink">{row.isNot}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT R. DALE BRUNET */}
      <section
        id="about-founder"
        className="bg-navy border-t border-slate-soft scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <SectionLabel number="04">About R. Dale Brunet</SectionLabel>

          {/* HEADER GRID — portrait + identity */}
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start mt-2">
            <div className="lg:col-span-5">
              <div
                data-testid="founder-headshot-frame"
                className="corner-bracket p-2 bg-slate-card-2 max-w-[280px] sm:max-w-[320px]"
              >
                <img
                  src={ASSETS.founderHeadshot}
                  alt={`${BRAND.founder} — ${BRAND.founderTitle}, ${BRAND.producer}`}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </div>
              <div className="mt-7">
                <h2 className="font-bebas text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                  R. Dale Brunet
                </h2>
                <p className="font-bebas text-gold tracking-[0.22em] text-sm sm:text-base uppercase mt-3">
                  {BRAND.founderTitle} · {BRAND.producer}
                </p>
                <p className="font-cormorant text-xl text-gold-bright/90 mt-2">
                  {BRAND.city}
                </p>
                <ul
                  data-testid="founder-roles"
                  className="flex flex-wrap gap-2 mt-6"
                >
                  {FOUNDER_ROLES.map((r) => (
                    <li
                      key={r}
                      className="font-bebas tracking-[0.16em] text-[11px] uppercase text-gold border border-gold/40 px-2.5 py-1"
                    >
                      {r}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="lg:col-span-7">
              <p className="font-cormorant text-2xl sm:text-3xl text-soft-gray leading-snug">
                R. Dale Brunet is an{" "}
                <span className="text-white">author</span>,{" "}
                <span className="text-white">publisher</span>,{" "}
                <span className="text-white">AI technologist</span>,{" "}
                <span className="text-white">software developer</span>,{" "}
                <span className="text-white">entrepreneur</span>,{" "}
                <span className="text-white">former restaurateur</span>, and the{" "}
                <span className="gold-text">founder &amp; producer of The Next Stage</span>.
              </p>
              <p className="font-manrope text-soft-gray mt-6 leading-relaxed text-lg">
                His professional life has moved across technology, hospitality,
                publishing, media, entrepreneurship, and community-building.
                That unusual combination is exactly what makes him suited to
                produce a platform like The Next Stage — a live public speaking
                competition and showcase that requires operational discipline,
                storytelling instinct, technology awareness, sponsor
                stewardship, speaker development, audience experience, and a
                clear sense of mission.
              </p>

              <div
                data-testid="founder-philosophy"
                className="corner-bracket card-dark p-7 mt-8"
              >
                <Quote className="text-gold mb-3" size={22} strokeWidth={1.6} />
                <p className="font-cormorant italic text-2xl lg:text-3xl text-gold-bright leading-snug">
                  &ldquo;Use what you got and make a lot.&rdquo;
                </p>
                <p className="font-bebas tracking-[0.22em] text-muted-ink text-xs uppercase mt-3">
                  His founding philosophy
                </p>
              </div>
            </div>
          </div>

          {/* CHAPTERS — life chapters that built the producer */}
          <div className="mt-16 lg:mt-20">
            <div className="label-bebas text-gold">The Chapters Behind The Stage</div>
            <h3 className="font-bebas text-white text-3xl sm:text-4xl leading-tight mt-3 max-w-3xl">
              Forty years of work that{" "}
              <span className="gold-text">prepared this platform.</span>
            </h3>
            <div
              data-testid="founder-chapters"
              className="grid md:grid-cols-2 gap-6 mt-10"
            >
              {FOUNDER_CHAPTERS.map((c) => {
                const Icon = c.icon;
                const slug = c.kicker
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")
                  .replace(/(^-|-$)/g, "");
                return (
                  <article
                    key={c.kicker}
                    data-testid={`founder-chapter-${slug}`}
                    className="card-dark p-7"
                  >
                    <div className="flex items-center gap-3">
                      <Icon
                        className="text-gold-bright shrink-0"
                        size={26}
                        strokeWidth={1.6}
                      />
                      <h4 className="font-shoulders font-black uppercase tracking-wider text-lg sm:text-xl text-white">
                        {c.kicker}
                      </h4>
                    </div>
                    <div className="gold-line" />
                    <p className="font-manrope text-soft-gray text-[15px] leading-relaxed mt-1">
                      {c.body}
                    </p>
                  </article>
                );
              })}
            </div>
          </div>

          {/* WHY THE NEXT STAGE — synthesis */}
          <div
            data-testid="founder-synthesis"
            className="mt-16 corner-bracket card-dark p-10 max-w-5xl"
          >
            <div className="label-bebas text-gold">Why The Next Stage</div>
            <h3 className="font-bebas text-white text-3xl sm:text-4xl mt-3 leading-tight">
              The Next Stage brings{" "}
              <span className="gold-text">these threads together.</span>
            </h3>
            <p className="font-manrope text-soft-gray mt-5 leading-relaxed text-lg">
              It is a live event — but also a media platform. It is a speaking
              competition — but also an entrepreneurial visibility engine. It is
              a community showcase — but also a structured pathway for emerging
              voices to be seen, heard, challenged, and elevated.
            </p>
            <p className="font-manrope text-soft-gray mt-4 leading-relaxed text-lg">
              Brunet understands what it means to stand behind a business in
              public — to build with limited resources, to create systems, tell
              stories, serve customers, manage pressure, and keep going when
              circumstances are not easy. Sponsors of The Next Stage are not
              being asked to support an untested personality with a vague dream.
              They are being invited to align with a founder who has built
              businesses, written books, developed technology, served the
              public, weathered setbacks, and continued creating.
            </p>
            <div className="gold-line my-7 max-w-md" />
            <p className="font-cormorant italic text-2xl lg:text-3xl text-gold-bright leading-snug">
              Use what you already carry — your story, your message, your
              experience, your courage — and take it to{" "}
              <span className="text-white not-italic font-bebas tracking-[0.04em]">
                the next stage.
              </span>
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/apply"
                data-testid="founder-cta-apply"
                className="btn-gold"
              >
                <Mic2 size={18} strokeWidth={1.8} />
                Apply To Speak
                <ArrowRight size={16} />
              </Link>
              <Link
                to="/sponsor"
                data-testid="founder-cta-sponsor"
                className="btn-outline"
              >
                Align As A Sponsor
              </Link>
            </div>
          </div>

          {/* PRODUCED BY — slim brand band */}
          <div className="mt-12 pt-6 border-t border-slate-soft flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="label-bebas text-gold mb-3">Produced By</div>
              <img
                src={ASSETS.producerLogo}
                alt={BRAND.producer}
                data-testid="about-producer-logo"
                className="w-full max-w-md h-auto object-contain"
                loading="lazy"
              />
              <p className="font-bebas tracking-[0.22em] text-soft-gray text-xs uppercase mt-3">
                {BRAND.producer}
              </p>
            </div>
            <Link
              to="/media-group"
              data-testid="about-media-group-link"
              className="btn-outline self-start sm:self-end"
            >
              <Building2 size={16} strokeWidth={1.8} />
              About The Next Stage Media Group
              <ArrowRight size={14} />
            </Link>
          </div>

          {/* Founding promise */}
          <p className="font-cormorant text-xl text-soft-gray italic mt-10 max-w-3xl">
            &ldquo;{PROMISES.contestants}&rdquo;
          </p>
        </div>
      </section>

      {/* PRINCIPLES */}
      <section className="bg-midnight border-t border-slate-soft">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
          <SectionLabel number="05">Eight Guiding Principles</SectionLabel>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
            {PRINCIPLES.map((p) => (
              <div key={p.h} className="card-dark p-6">
                <div className="flex items-center gap-3">
                  <CardMark className="h-8 sm:h-10" />
                  <h3 className="font-shoulders font-bold uppercase tracking-wider text-xl text-white">
                    {p.h}
                  </h3>
                </div>
                <div className="gold-line" />
                <p className="font-manrope text-soft-gray text-sm mt-2 leading-relaxed">
                  {p.p}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-navy border-t border-slate-soft">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-14 text-center">
          <h2 className="font-bebas text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            Ready To Step <span className="gold-text">Forward?</span>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/apply" data-testid="about-cta-apply" className="btn-gold">
              <Mic2 size={18} strokeWidth={1.8} />
              Apply To Speak
              <ArrowRight size={16} />
            </Link>
            <Link to="/contact" data-testid="about-cta-contact" className="btn-outline">
              <MessageCircle size={18} strokeWidth={1.8} />
              Contact The Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
